/**
 * BackgammonLogic — a VERBATIM TypeScript port of Client/Assets/Code/Games/BackgammonLogic.cs, the
 * shared engine that runs identically on the Unity client and the game server. The web client runs it
 * locally and optimistically: it computes legal-move suggestions (showAvailableOptions), applies its
 * own and the opponent's moves (moveBead), and keeps board state between full GetGame snapshots. Any
 * behavioural drift from the C# means the server rejects moves this client thinks are legal — so the
 * non-obvious C# semantics are reproduced exactly and flagged with comments. Do NOT "clean up".
 */

import {
  BeadColor,
  BoardState,
  BACKGAMMON_START_ROW,
  BACKGAMMON_ROWS_COUNT,
  BACKGAMMON_WAIT_ROW_WHITE,
  BACKGAMMON_WAIT_ROW_BLACK,
  BACKGAMMON_OUT_ROW_WHITE,
  BACKGAMMON_OUT_ROW_BLACK,
} from './constants';
import { DicePack, BeadPack, UndoPack, MovePacket, type HighLightColumnDto } from './types';
import {
  destination,
  isValidGameRow,
  isInBlock,
  hasWaitingBead,
  hasBeadsInGameRows,
  haveSameItems,
  addDice,
} from './extensions';

export class BackgammonLogic {
  p1Color: BeadColor = BeadColor.NotSet;
  p2Color: BeadColor = BeadColor.NotSet;

  private turnClr: BeadColor = BeadColor.White;
  undo: UndoPack = new UndoPack();

  selectedRow: number | null = null;
  dice: DicePack = new DicePack();
  beads: BeadPack = new BeadPack();

  /** Per-instance replacement for the C# static BeadTransferEvent — the scene hooks this to animate. */
  onBeadTransfer?: (from: number, lastBeadNum: number, to: number) => void;

  get turnColor(): BeadColor {
    return this.turnClr;
  }

  /** Setting a DIFFERENT colour clears the undo history (C# TurnColor setter). Same colour = no-op. */
  set turnColor(value: BeadColor) {
    if (value !== this.turnClr) {
      this.undo.clear();
      this.turnClr = value;
    }
  }

  /**
   * Set the turn colour WITHOUT clearing undo — only for wire deserialization, mirroring the C#
   * Deserialize which assigns the private field directly (so a decoded GetGame keeps its UndoPack).
   */
  setTurnColorInternal(value: BeadColor): void {
    this.turnClr = value;
  }

  resetGame(): void {
    this.turnColor = BeadColor.NotSet;
    this.beads.setupStandardGame();
    this.dice.readyToThrowDice();
  }

  switchTurn(): void {
    this.turnColor =
      this.turnClr === BeadColor.White
        ? BeadColor.Black
        : this.turnClr === BeadColor.Black
          ? BeadColor.White
          : this.turnClr;
  }

  // --- move generation -------------------------------------------------------------------------

  /**
   * Recursive backtracking over the available dice. CRITICAL aliasing quirk preserved from C#:
   * `availableMoves.push(currentMove)` pushes a REFERENCE that keeps being mutated in place across the
   * loop; the recursion is handed `currentMove.clone()`. Do not clone before push.
   */
  private getAvailableMoves(
    rowIndex: number,
    beadColor: BeadColor,
    availableMoves: MovePacket[],
    dices: number[],
    currentMove: MovePacket | null = null,
  ): void {
    if (dices.length === 0) {
      return;
    }

    rowIndex = currentMove?.last()?.to ?? rowIndex;

    for (const dice of [...dices]) {
      const target = destination(this.beads.gameRows[rowIndex], dice, beadColor);
      // C# `HitAndRunCheck(...) != false` === `=== true`. canMove(null) short-circuits the target! read.
      if (!this.canMove(target) || this.hitAndRunCheck(rowIndex, target!) === true) {
        continue;
      }

      let shouldErase = false;
      if (currentMove === null) {
        currentMove = new MovePacket(rowIndex, target!, dice);
        shouldErase = true;
      } else {
        currentMove.add(rowIndex, target!, dice);
      }

      availableMoves.push(currentMove);

      const clonedDices = [...dices];
      const index = clonedDices.indexOf(dice);
      if (index >= 0) {
        clonedDices.splice(index, 1);
        this.getAvailableMoves(target!, beadColor, availableMoves, [...clonedDices], currentMove.clone());
      }

      if (shouldErase) {
        currentMove = null;
      }
    }
  }

  availableMoves(): MovePacket[] {
    const availableMoves: MovePacket[] = [];
    if (this.dice.availables.length === 0) {
      return availableMoves;
    }

    const tableState = this.getTableState();

    switch (tableState) {
      case BoardState.WhiteAllowMove:
      case BoardState.BlackAllowMove: {
        for (const row of this.beads.gameRows.filter((g) => g.color === this.turnColor)) {
          this.getAvailableMoves(row.rowIndex, row.color, availableMoves, [...this.dice.availables]);
        }
        break;
      }
      case BoardState.WhiteWaiting: {
        for (const dice of this.dice.availables) {
          const target = BACKGAMMON_START_ROW - 1 + dice;
          if (this.canMove(target)) {
            availableMoves.push(new MovePacket(BACKGAMMON_WAIT_ROW_WHITE, target, dice));
          }
        }
        break;
      }
      case BoardState.BlackWaiting: {
        for (const dice of this.dice.availables) {
          const target = BACKGAMMON_START_ROW + BACKGAMMON_ROWS_COUNT - dice;
          if (this.canMove(target)) {
            availableMoves.push(new MovePacket(BACKGAMMON_WAIT_ROW_BLACK, target, dice));
          }
        }
        break;
      }
      case BoardState.WhiteAllowToMoveOut:
      case BoardState.BlackAllowToMoveOut: {
        const lastRow = this.findLastEmptyRow(); // furthest occupied home row (needs 6 to bear off)
        for (const row of this.beads.gameRows.filter((g) => g.color === this.turnColor)) {
          for (const dice of this.dice.availables) {
            const virtualDice = dice > lastRow ? lastRow : dice;
            const target = destination(row, virtualDice);
            if (this.canMove(target, true) && this.hitAndRunCheck(row.rowIndex, target!) === false) {
              availableMoves.push(new MovePacket(row.rowIndex, target!, dice));
            }
          }
        }
        break;
      }
    }

    // Dedup by `${start}-${end}-${dices#join}`, preserving first occurrence (C# GroupBy().First()).
    const seen = new Set<string>();
    const result: MovePacket[] = [];
    for (const a of availableMoves) {
      const key = `${a.start}-${a.end}-${a.dices.join('#')}`;
      if (!seen.has(key)) {
        seen.add(key);
        result.push(a);
      }
    }
    return result;
  }

  private findLastEmptyRow(): number {
    let lastRow = 6;
    for (let i = 6; i > 0; i--) {
      let index = 0;
      if (this.turnColor === BeadColor.Black) {
        index = BACKGAMMON_START_ROW - 1 + i;
      }
      if (this.turnColor === BeadColor.White) {
        index = BACKGAMMON_START_ROW + BACKGAMMON_ROWS_COUNT - i;
      }
      if (this.beads.gameRows[index].count > 0 && this.beads.gameRows[index].color === this.turnColor) {
        lastRow = i;
        break;
      }
    }
    return lastRow;
  }

  availableRows(): number[] {
    return [...new Set(this.availableMoves().map((m) => m.start))];
  }

  getTableState(): BoardState {
    if (hasWaitingBead(this.beads, this.turnColor)) {
      return this.turnColor === BeadColor.Black ? BoardState.BlackWaiting : BoardState.WhiteWaiting;
    } else if (hasBeadsInGameRows(this.beads, this.turnColor)) {
      return this.turnColor === BeadColor.Black ? BoardState.BlackAllowMove : BoardState.WhiteAllowMove;
    } else {
      return this.turnColor === BeadColor.Black
        ? BoardState.BlackAllowToMoveOut
        : BoardState.WhiteAllowToMoveOut;
    }
  }

  canMove(target: number | null, moveOut = false): boolean {
    if (target === null) {
      return false;
    }
    if (isValidGameRow(target)) {
      return this.isValidMoveToGameRow(target);
    } else if (this.isValidWaitingRow(target)) {
      return true;
    } else if (this.isValidOutRow(target)) {
      return moveOut;
    }
    return false;
  }

  private isValidMoveToGameRow(target: number): boolean {
    return this.beads.gameRows[target].count < 2 || this.beads.gameRows[target].color === this.turnColor;
  }

  private isValidWaitingRow(target: number): boolean {
    return target === BACKGAMMON_WAIT_ROW_BLACK || target === BACKGAMMON_WAIT_ROW_WHITE;
  }

  private isValidOutRow(target: number): boolean {
    return target === BACKGAMMON_OUT_ROW_WHITE || target === BACKGAMMON_OUT_ROW_BLACK;
  }

  calculateTargetRowBasedOnDice(row: number, diceDistance: number): number | null {
    const availableMoves = this.availableMoves();
    const match = availableMoves.find(
      (m) => m.start === row && m.dices.reduce((a, b) => a + b, 0) === diceDistance,
    );
    return match ? match.end : null;
  }

  private isAllowMoveState(tableState: BoardState): boolean {
    return tableState === BoardState.BlackAllowMove || tableState === BoardState.WhiteAllowMove;
  }

  private isAllowMoveOutState(tableState: BoardState): boolean {
    return (
      tableState === BoardState.BlackAllowToMoveOut || tableState === BoardState.WhiteAllowToMoveOut
    );
  }

  private isWhiteWaiting(tableState: BoardState): boolean {
    return tableState === BoardState.WhiteWaiting && this.turnColor === BeadColor.White;
  }

  private isBlackWaiting(tableState: BoardState): boolean {
    return tableState === BoardState.BlackWaiting && this.turnColor === BeadColor.Black;
  }

  // --- suggestions (the "highlight legal destinations" UX) -------------------------------------

  showAvailableOptions(row: number): HighLightColumnDto[] {
    this.selectedRow = row;
    const tableState = this.getTableState();

    if (this.isWhiteWaiting(tableState)) {
      return this.showAvailableOptionsForWaiting(BACKGAMMON_WAIT_ROW_WHITE);
    }
    if (this.isBlackWaiting(tableState)) {
      return this.showAvailableOptionsForWaiting(BACKGAMMON_WAIT_ROW_BLACK);
    }

    const allowMoveOut = this.isAllowMoveOutState(tableState);
    switch (this.dice.availables.length) {
      case 4:
      case 3:
        // all dice equal → the target can be reached cumulatively with i+1 of them
        return this.multiDiceOptions(tableState);
      case 2:
        return this.twoDiceOptions(tableState);
      case 1:
        return this.oneDiceOptions(allowMoveOut);
      default:
        return [];
    }
  }

  /**
   * The C# case 4/3 body. Its `break;` statements break the SWITCH (terminating the case), which here
   * becomes an early `return`. diceNeed accumulates and is SNAPSHOTTED into each DTO ([...diceNeed]).
   */
  private multiDiceOptions(tableState: BoardState): HighLightColumnDto[] {
    const result: HighLightColumnDto[] = [];
    const diceNeed: number[] = [];
    const diceCount = this.dice.availables.length;

    if (this.isAllowMoveState(tableState)) {
      for (let i = 0; i < diceCount; i++) {
        const distance = this.dice.availables[i] * (i + 1);
        const dice = this.dice.availables[i];
        const targetRow = this.canMoveAndHitAndRunCheck(tableState, distance);
        if (targetRow !== null) {
          diceNeed.push(dice);
          result.push({ targetRow, diceNeed: [...diceNeed] });
        } else {
          return result; // C# break (switch)
        }
      }
    } else {
      const dice = this.dice.availables[0];
      const targetRow = this.canMoveAndHitAndRunCheck(tableState, dice);
      if (targetRow !== null) {
        diceNeed.push(this.dice.availables[0]);
        result.push({ targetRow, diceNeed: [...diceNeed] });
      } else {
        return result; // C# break (switch)
      }
    }

    return result;
  }

  private twoDiceOptions(tableState: BoardState): HighLightColumnDto[] {
    const result: HighLightColumnDto[] = [];
    const a0 = this.dice.availables[0];
    const a1 = this.dice.availables[1];

    const targetForFirstDice = this.canMoveAndHitAndRunCheck(tableState, a0);
    if (targetForFirstDice !== null) {
      result.push({ targetRow: targetForFirstDice, diceNeed: [a0] });
    }

    const targetForSecondDice = this.canMoveAndHitAndRunCheck(tableState, a1);
    if (targetForSecondDice !== null) {
      result.push({ targetRow: targetForSecondDice, diceNeed: [a1] });
    }

    if (targetForFirstDice !== null || targetForSecondDice !== null) {
      const distance = addDice(a1, a0);
      const targetForBothDice = this.canMoveAndHitAndRunCheck(tableState, distance);
      if (targetForBothDice !== null) {
        result.push({ targetRow: targetForBothDice, diceNeed: [...this.dice.availables] });
      }
    }

    return result;
  }

  private oneDiceOptions(allowMoveOut: boolean): HighLightColumnDto[] {
    const result: HighLightColumnDto[] = [];
    const a0 = this.dice.availables[0];
    const targetRow = this.calculateTargetRowBasedOnDice(this.selectedRow!, a0);
    if (this.canMove(targetRow, allowMoveOut) && this.hitAndRunCheck(this.selectedRow!, targetRow!) === false) {
      result.push({ targetRow: targetRow!, diceNeed: [a0] });
    }
    return result;
  }

  private canMoveAndHitAndRunCheck(boardState: BoardState, diceDistance: number): number | null {
    const allowMoveOut = this.isAllowMoveOutState(boardState);
    const targetRow = this.calculateTargetRowBasedOnDice(this.selectedRow!, diceDistance);
    if (this.canMove(targetRow, allowMoveOut) && this.hitAndRunCheck(this.selectedRow!, targetRow!) === false) {
      return targetRow;
    }
    return null;
  }

  private showAvailableOptionsForWaiting(selectRow: number): HighLightColumnDto[] {
    this.selectedRow = selectRow;
    const result: HighLightColumnDto[] = [];
    for (const dice of this.dice.availables) {
      const targetRow = this.calculateTargetRowBasedOnDice(this.selectedRow!, dice);
      if (this.canMove(targetRow)) {
        result.push({ targetRow: targetRow!, diceNeed: [dice] });
      }
    }
    return result;
  }

  // --- applying moves --------------------------------------------------------------------------

  undoMove(): boolean {
    if (this.undo.count() <= 0) {
      return false;
    }

    const fetchedUndo = this.undo.fetch()!;
    this.transferBead(fetchedUndo.to, fetchedUndo.from);
    this.dice.restoreDice(fetchedUndo.dice);

    if (fetchedUndo.hit) {
      // restore the opponent blot that was sent to its wait row
      const waitRow =
        this.beads.gameRow(fetchedUndo.from).color === BeadColor.White
          ? BACKGAMMON_WAIT_ROW_BLACK
          : BACKGAMMON_WAIT_ROW_WHITE;
      this.transferBead(waitRow, fetchedUndo.to);
    }
    return true;
  }

  hitAndRunCheck(pos: number, target: number): boolean {
    if (!this.isValidTargetRow(target) || !this.isInCorrectBlock(target)) {
      return false;
    }
    const res = this.undo.checkFreezeBead(pos);
    return this.isInCorrectBlock(res);
  }

  private isValidTargetRow(target: number): boolean {
    return this.beads.gameRow(target).count >= 1 && this.beads.gameRow(target).color === this.turnColor;
  }

  private isInCorrectBlock(target: number): boolean {
    return (
      (this.turnColor === BeadColor.Black && isInBlock(target, 1)) ||
      (this.turnColor === BeadColor.White && isInBlock(target, 4))
    );
  }

  moveBead(selectedRow: number, dices: number[]): MovePacket[] {
    const moves: MovePacket[] = [];
    const availableMoves = this.availableMoves();
    const movePacket = availableMoves.find(
      (m) => m.start === selectedRow && haveSameItems(m.dices, dices),
    );

    if (movePacket == null) {
      return moves;
    }

    for (const move of movePacket) {
      const dice = move.dice;
      const from = move.from;
      const to = move.to;

      this.dice.useDice(dice);
      if (this.beads.gameRow(to).count === 1 && this.beads.gameRow(to).color !== this.turnColor) {
        // hit: send the opponent blot to its own wait row, then land our checker
        const waitRow =
          this.beads.gameRow(to).color === BeadColor.White
            ? BACKGAMMON_WAIT_ROW_WHITE
            : BACKGAMMON_WAIT_ROW_BLACK;
        this.transferBead(to, waitRow);
        this.transferBead(from, to);
        this.undo.insert(from, to, dice, true);
      } else {
        this.transferBead(from, to);
        this.undo.insert(from, to, dice);
      }
    }

    moves.push(movePacket);
    return moves;
  }

  transferBead(from: number, to: number): void {
    const fromRow = this.beads.gameRow(from);
    const lastBeadNum = fromRow.count - 1; // computed BEFORE removal
    this.beads.gameRow(to).addBead(1, fromRow.color);
    fromRow.removeBead(1);
    this.onBeadTransfer?.(from, lastBeadNum, to);
  }

  /**
   * Whether `color` has borne off all 15 checkers (ported from server Backgammon.AllBeadsAreOut). The
   * scene maps a Role to a colour via p1Color/p2Color. Throws on an impossible state, matching C#.
   */
  allBeadsAreOut(color: BeadColor): boolean {
    if (this.beads.gameRows.some((i) => i.color === color)) {
      return false;
    }
    if (this.beads.waitRow.get(color)!.count > 0) {
      return false;
    }
    if (this.beads.outRow.get(color)!.count > 0) {
      return true;
    }
    throw new Error('Invalid game state');
  }
}
